import React from 'react';
import { Star, Trophy, Zap, Shield, TrendingUp, Clock, AlertCircle } from 'lucide-react';
import FAQSection from '../components/FAQSection';
import SEOHelmet from '../components/SEOHelmet';
import CollapsibleSEO from '../components/CollapsibleSEO';
import './Sportwetten.css';

const Sportwetten = () => {
  const advantages = [
    { icon: <Trophy />, title: 'Top-Quoten', description: 'Die besten Wettquoten für alle Top-Ligen' },
    { icon: <Zap />, title: 'Live-Wetten', description: 'Wetten Sie live auf Ihre Lieblingssportarten' },
    { icon: <Shield />, title: 'Sicher & Lizenziert', description: 'Vollständig lizenziert und reguliert' },
    { icon: <Clock />, title: '24/7 Verfügbar', description: 'Wetten Sie jederzeit auf Tausende von Events' },
    { icon: <TrendingUp />, title: 'Cashout-Funktion', description: 'Sichern Sie Ihre Gewinne vorzeitig' },
    { icon: <Star />, title: 'Bonus-Angebote', description: 'Regelmäßige Promotionen für Sportwetter' }
  ];

  const sportsCategories = [
    { name: 'Fußball', events: '10.000+', description: 'Bundesliga, Champions League, und mehr' },
    { name: 'Tennis', events: '5.000+', description: 'Grand Slams, ATP, WTA Turniere' },
    { name: 'Basketball', events: '3.000+', description: 'NBA, EuroLeague, BBL' },
    { name: 'Eishockey', events: '2.500+', description: 'NHL, DEL, Champions Hockey League' },
    { name: 'Handball', events: '1.500+', description: 'Bundesliga, Champions League' },
    { name: 'E-Sports', events: '2.000+', description: 'CS:GO, Dota 2, League of Legends' }
  ];

  const faqData = [
    {
      question: 'Ist N1 Bet sicher und lizenziert?',
      answer: 'Ja, N1 Bet ist vollständig lizenziert und reguliert. Wir verwenden modernste SSL-Verschlüsselungstechnologie, um Ihre persönlichen Daten und Transaktionen zu schützen. Alle Wettmärkte werden fair und transparent gehandhabt.'
    },
    {
      question: 'Welche Sportarten kann ich bei N1 Bet wetten?',
      answer: 'Bei N1 Bet können Sie auf über 30 verschiedene Sportarten wetten, darunter Fußball, Tennis, Basketball, Eishockey, Handball, American Football, Baseball, E-Sports und viele mehr. Wir bieten täglich Tausende von Wettmärkten aus der ganzen Welt.'
    },
    {
      question: 'Bietet N1 Bet Live-Wetten an?',
      answer: 'Ja, N1 Bet bietet umfangreiche Live-Wetten auf alle wichtigen Sportveranstaltungen. Sie können während des Spiels auf sich ändernde Quoten wetten und die Action in Echtzeit mitverfolgen. Unsere Live-Wetten-Plattform ist schnell, zuverlässig und bietet eine Vielzahl von Wettoptionen.'
    },
    {
      question: 'Was ist die Cashout-Funktion bei N1 Bet?',
      answer: 'Die Cashout-Funktion ermöglicht es Ihnen, Ihre Wette vorzeitig abzuschließen, bevor das Event beendet ist. Dies gibt Ihnen die Möglichkeit, Gewinne zu sichern oder Verluste zu minimieren. Der Cashout-Betrag wird in Echtzeit basierend auf dem aktuellen Spielstand und den Quoten berechnet.'
    },
    {
      question: 'Welche Zahlungsmethoden werden bei N1 Bet akzeptiert?',
      answer: 'N1 Bet akzeptiert eine Vielzahl von Zahlungsmethoden, darunter Kreditkarten (Visa, Mastercard), E-Wallets (Skrill, Neteller), Banktransfers und Prepaid-Karten. Einzahlungen werden sofort verarbeitet, und Auszahlungen erfolgen in der Regel innerhalb von 24 Stunden.'
    },
    {
      question: 'Gibt es einen Willkommensbonus für Sportwetten bei N1 Bet?',
      answer: 'Ja, neue Kunden bei N1 Bet erhalten einen attraktiven Willkommensbonus für Sportwetten. Die genauen Bonusbedingungen finden Sie auf der Promotions-Seite. Zusätzlich bieten wir regelmäßig spezielle Angebote für große Sportevents wie die Champions League oder Weltmeisterschaften.'
    }
  ];

  // Schema.org markup for Sportwetten page (after faqData is defined)
  const schemaOrgSportsBook = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "N1 Bet",
    "url": "https://n1casino-review.com/sportwetten",
    "logo": "https://eu2007.de/?attachment_id=18472",
    "description": "N1 Bet - Die führende Sportwetten-Plattform in Deutschland mit über 30 Sportarten",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "9.4",
      "bestRating": "10",
      "ratingCount": "3500"
    }
  };

  const schemaOrgFAQ = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="sportwetten">
      <SEOHelmet 
        title="N1 Bet Sportwetten 2025 - N1 Bet Sport Wetten Deutschland | N1Bet Erfahrungen"
        description="N1 Bet Sportwetten Review ⚽ Über 30 Sportarten ✓ Top-Quoten ✓ Live-Wetten ✓ N1Bet Sport ✓ N1 Bet Wetten ✓ Cashout-Funktion ✓ Offizielle N1Bet Bewertung"
        keywords="n1 bet sport, n1bet sportwetten, n1 bet wetten, n1bet sport, n1bet wetten, n1 bet sportwetten"
        canonical="https://n1casino-review.com/sportwetten"
        schema={[schemaOrgSportsBook, schemaOrgFAQ]}
      />
      {/* Hero Section with Banner */}
      <section className="hero-section hero-banner">
        <div className="hero-banner-image">
          <img 
            src="https://customer-assets.emergentagent.com/job_glucksspiel-n1/artifacts/ddlj3sjd_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-11%20%D0%B2%2022.41.41.png"
            alt="N1 Bet Banner"
          />
        </div>
        <div className="hero-container">
          <div className="hero-content">
            <div className="rating-badge">
              <Star fill="#DC0000" color="#DC0000" size={20} />
              <Star fill="#DC0000" color="#DC0000" size={20} />
              <Star fill="#DC0000" color="#DC0000" size={20} />
              <Star fill="#DC0000" color="#DC0000" size={20} />
              <Star fill="#DC0000" color="#DC0000" size={20} />
              <span className="rating-text">9.4/10 Bewertung</span>
            </div>
            <h1 className="hero-title">
              N1 Bet – Die führende<br />
              Sportwetten-Plattform in Deutschland
            </h1>
            <p className="hero-subtitle">
              Wetten Sie auf über 30 Sportarten mit Top-Quoten, Live-Wetten und einer 
              benutzerfreundlichen Plattform. Erleben Sie Sportwetten auf höchstem Niveau.
            </p>
            <div className="hero-cta">
              <button className="btn-primary">Jetzt wetten</button>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="advantages-section section">
        <div className="container">
          <h2 className="section-title">Warum N1 Bet wählen?</h2>
          <p className="section-subtitle">
            Entdecken Sie die vielen Vorteile der führenden Sportwetten-Plattform
          </p>
          <div className="advantages-grid">
            {advantages.map((advantage, index) => (
              <div key={index} className="advantage-card">
                <div className="advantage-icon">{advantage.icon}</div>
                <h3>{advantage.title}</h3>
                <p>{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sports Categories with Banner */}
      <section className="sports-section section sports-banner">
        <div className="sports-banner-image">
          <img 
            src="https://customer-assets.emergentagent.com/job_glucksspiel-n1/artifacts/35idyxqb_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-11-11%20%D0%B2%2022.42.03.png"
            alt="Sports Banner"
          />
        </div>
        <div className="container">
          <h2 className="section-title">Wetten Sie auf über 30 Sportarten</h2>
          <p className="section-subtitle">
            Von Fußball bis E-Sports – finden Sie Ihre Lieblingssportart
          </p>
          <div className="sports-grid">
            {sportsCategories.map((sport, index) => (
              <div key={index} className="sport-card">
                <Trophy size={40} color="#DC0000" />
                <h3>{sport.name}</h3>
                <div className="sport-events">{sport.events}</div>
                <p>{sport.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bonus Section */}
      <section className="bonus-section section">
        <div className="container">
          <div className="bonus-card">
            <div className="bonus-content">
              <h2>Willkommensbonus für Sportwetten</h2>
              <p>
                Starten Sie Ihre Wett-Reise bei N1 Bet mit einem attraktiven Willkommensbonus. 
                Profitieren Sie von regelmäßigen Promotionen und speziellen Angeboten für große Sportevents.
              </p>
              <button className="btn-primary">Jetzt Bonus sichern</button>
              <p className="bonus-terms">18+ | AGB gelten | Verantwortungsbewusst wetten</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section - Collapsible, Moved before FAQ */}
      <section className="seo-content-section">
        <div className="container">
          <CollapsibleSEO
            title="N1 Bet: Ihre erste Wahl für erstklassige Sportwetten"
            firstParagraph={
              <p>
                N1 Bet hat sich als eine der führenden Sportwetten-Plattformen in Deutschland etabliert und bietet 
                Wettbegeisterten eine erstklassige Wettumgebung. Mit über 30 verschiedenen Sportarten und täglich 
                Tausenden von Wettmärkten können Sie bei N1 Bet auf alle großen Sportevents weltweit wetten. Von der 
                Bundesliga über die Champions League bis hin zu internationalen Tennisturnieren und NBA-Spielen – 
                N1 Bet deckt das gesamte Spektrum des Sports ab.
              </p>
            }
          >
            <div className="content-grid">
            <div className="content-block">
              <p>
                Die Plattform zeichnet sich durch ihre benutzerfreundliche Oberfläche, schnelle Ladezeiten und intuitive 
                Navigation aus. Egal ob Sie ein erfahrener Wetter oder ein Neuling sind, N1 Bet macht es Ihnen leicht, 
                Ihre Lieblingssportarten zu finden und Wetten zu platzieren. Die mobiloptimierte Website funktioniert 
                einwandfrei auf allen Geräten, sodass Sie jederzeit und überall wetten können.
              </p>
            </div>

            <div className="content-block">
              <h3>Top-Quoten und umfangreiche Wettmärkte bei N1 Bet</h3>
              <p>
                N1 Bet ist bekannt für seine wettbewerbsfähigen Quoten, die zu den besten auf dem Markt gehören. 
                Unsere Quotenexperten arbeiten kontinuierlich daran, Ihnen die attraktivsten Quoten für alle wichtigen 
                Sportevents anzubieten. Ob Fußball-Bundesliga, Champions League, Premier League oder internationale 
                Turniere – bei N1 Bet erhalten Sie immer faire und wettbewerbsfähige Quoten.
              </p>
              <p>
                Neben den Standard-Wettmärkten wie 1X2, Über/Unter und Handicap bietet N1 Bet auch eine Vielzahl von 
                Spezialwetten an. Sie können auf Torschützen, Ecken, Karten, Halbzeitergebnisse und viele weitere 
                Märkte wetten. Für große Events wie die Fußball-Weltmeisterschaft oder Champions League-Finals bietet 
                N1 Bet oft über 500 verschiedene Wettmöglichkeiten für ein einzelnes Spiel.
              </p>
            </div>

            <div className="content-block">
              <h3>Live-Wetten bei N1 Bet: Action in Echtzeit</h3>
              <p>
                Eine der Stärken von N1 Bet ist das umfangreiche Live-Wetten-Angebot. Sie können während des laufenden 
                Spiels auf sich ändernde Quoten wetten und die Entwicklung des Events in Echtzeit mitverfolgen. Die 
                Live-Wetten-Plattform von N1 Bet bietet detaillierte Statistiken, Live-Scores und grafische 
                Darstellungen, die Ihnen helfen, fundierte Wettentscheidungen zu treffen.
              </p>
              <p>
                Täglich stehen Hunderte von Live-Events zur Verfügung, von Fußballspielen aus den Top-Ligen bis hin zu 
                Tennis, Basketball, Eishockey und E-Sports. Die Quoten werden in Echtzeit aktualisiert, und Sie können 
                schnell und einfach Ihre Wetten platzieren. Die innovative Cashout-Funktion gibt Ihnen zudem die 
                Möglichkeit, Ihre Wetten vorzeitig abzuschließen und Gewinne zu sichern oder Verluste zu minimieren.
              </p>
            </div>

            <div className="content-block">
              <h3>Vielfältiges Sportangebot bei N1 Bet</h3>
              <p>
                N1 Bet deckt über 30 verschiedene Sportarten ab und bietet somit für jeden Sportfan die passenden 
                Wettmöglichkeiten. Fußball ist natürlich die beliebteste Sportart mit umfangreichen Märkten für alle 
                großen Ligen und Turniere weltweit. Von der Bundesliga über die Premier League bis zur Champions League 
                und internationalen Wettbewerben – bei N1 Bet finden Sie alle wichtigen Fußball-Events.
              </p>
              <p>
                Tennis-Fans können auf alle Grand-Slam-Turniere, ATP- und WTA-Events wetten. Basketball-Enthusiasten 
                finden umfangreiche Märkte für die NBA, EuroLeague und BBL. Auch Eishockey, Handball, American Football, 
                Baseball, Volleyball und viele weitere Sportarten sind bei N1 Bet verfügbar. Ein besonderes Highlight ist 
                das E-Sports-Angebot mit Wettmöglichkeiten für beliebte Spiele wie CS:GO, Dota 2, League of Legends und mehr.
              </p>
            </div>

            <div className="content-block">
              <h3>Sichere Zahlungen und schnelle Auszahlungen</h3>
              <p>
                Bei N1 Bet stehen Ihnen zahlreiche sichere und zuverlässige Zahlungsmethoden zur Verfügung. Sie können 
                zwischen Kreditkarten (Visa, Mastercard), E-Wallets (Skrill, Neteller, ecoPayz), Banktransfers und 
                Prepaid-Karten wählen. Alle Einzahlungen werden sofort verarbeitet, sodass Sie ohne Verzögerung mit dem 
                Wetten beginnen können.
              </p>
              <p>
                Auszahlungen bei N1 Bet werden schnell und zuverlässig bearbeitet. Die meisten Auszahlungsanfragen werden 
                innerhalb von 24 Stunden genehmigt. Bei E-Wallets kann das Geld oft noch am selben Tag auf Ihrem Konto 
                sein. N1 Bet erhebt keine Gebühren für Ein- oder Auszahlungen, was die Plattform besonders attraktiv macht. 
                Die Mindesteinzahlung ist niedrig angesetzt, sodass auch Gelegenheitswetter problemlos teilnehmen können.
              </p>
            </div>

            <div className="content-block">
              <h3>Kundenservice und verantwortungsvolles Wetten</h3>
              <p>
                Der Kundenservice von N1 Bet ist rund um die Uhr per Live-Chat und E-Mail erreichbar. Das freundliche und 
                kompetente Support-Team hilft Ihnen bei allen Fragen zu Ihrem Konto, Wetten, Zahlungen oder technischen 
                Problemen. Die Antwortzeiten sind kurz, und das Team spricht fließend Deutsch.
              </p>
              <p>
                N1 Bet nimmt das Thema verantwortungsvolles Wetten sehr ernst. Die Plattform bietet verschiedene Tools zur 
                Selbstkontrolle, darunter Einzahlungslimits, Wettlimits, Selbstausschlussoptionen und Reality-Checks. 
                Spieler unter 18 Jahren sind vom Wetten ausgeschlossen. N1 Bet arbeitet mit Organisationen zur 
                Prävention von Spielsucht zusammen und bietet Unterstützung für Spieler, die Hilfe benötigen.
              </p>
            </div>
          </div>
          </CollapsibleSEO>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section section">
        <div className="container">
          <h2 className="section-title">Häufig gestellte Fragen zu N1 Bet</h2>
          <FAQSection faqs={faqData} />
        </div>
      </section>
    </div>
  );
};

export default Sportwetten;
